<template>
  <div v-if="model">
    <div class="col-lg-12 col-md-12 col-xl-10 col-xxl-7">
      <div v-if="empresaInmobiliaria.IdEstado" class="empresa-estado-section mb-3">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <span class="dx-field-item-label-text">{{ $t("mis_datos.mi_empresa_inmobiliaria.estado") }}</span>
          <span :class="['status-badge', 'estado-empresa-agente', empresaInmobiliaria.IdEstado]">
            {{ $t(`empresa_agente.estado.${empresaInmobiliaria.IdEstado}`) }}
          </span>
          <span class="info-icon d-inline text-primary">
            <i class="fas fa-info-circle" :id="estadoEmpresaTooltipId"></i>
            <DxTooltip :hide-on-outside-click="false" :target="`#${estadoEmpresaTooltipId}`" show-event="mouseenter"
              hide-event="mouseleave">
              <template #content>
                <div>{{ $t(`empresa_agente_estado_tooltip.${empresaInmobiliaria.IdEstado}`) }}</div>
              </template>
            </DxTooltip>
          </span>
        </div>
        <div v-if="esEmpresaRechazada && empresaInmobiliaria.MotivoRechazo" class="mt-2">
          <span class="dx-field-item-label-text d-block">{{ $t("mis_datos.mi_empresa_inmobiliaria.motivo_rechazo") }}</span>
          <span class="mt-1 d-block">{{ empresaInmobiliaria.MotivoRechazo }}</span>
        </div>
      </div>
      <DxForm id="formularioEmpresaInmobiliaria" ref="formInstance" :form-data="model" label-location="top"
        :show-colon-after-label="false" validation-group="validacionEmpresaInmobiliaria">
        <!-- xs: 768, sm: 992, md:1200 -->
        <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

        <!-- Datos de la empresa -->
        <DxGroupItem :col-count="6" :col-span="6">
          <DxSimpleItem data-field="RazonSocial" editor-type="dxTextBox" :col-span="3">
            <DxLabel :text="$t('empresas_inmobiliarias.form.razon_social')" />
            <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.razon_social')" />
            <DxCustomRule
              :validation-callback="validarRazonSocial"
              :message="$t('empresas_inmobiliarias.form.validacion.campo_no_modificable_tras_validacion')"
              :reevaluate="true"
            />
          </DxSimpleItem>
          <DxSimpleItem data-field="Documento" editor-type="dxTextBox" :col-span="3">
            <DxLabel :text="$t('empresas_inmobiliarias.form.cif')" />
            <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.cif')" />
            <DxPatternRule :pattern="cifPattern" :message="$t('empresas_inmobiliarias.form.validacion.cif_patron')" />
            <DxCustomRule
              :validation-callback="validarDocumento"
              :message="$t('empresas_inmobiliarias.form.validacion.campo_no_modificable_tras_validacion')"
              :reevaluate="true"
            />
          </DxSimpleItem>
          <DxSimpleItem data-field="Web" editor-type="dxTextBox" :col-span="6">
            <DxLabel :text="$t('empresas_inmobiliarias.form.sitio_web')" />
            <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.sitio_web')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Telefono" editor-type="dxTextBox" :col-span="3">
            <DxLabel :text="$t('empresas_inmobiliarias.form.telefono')" />
            <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.telefono')" />
            <DxPatternRule :pattern="telefonoPattern"
              :message="$t('empresas_inmobiliarias.form.validacion.telefono_patron')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Email" editor-type="dxTextBox" :col-span="3">
            <DxLabel :text="$t('empresas_inmobiliarias.form.email')" />
            <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.email')" />
            <DxEmailRule :message="$t('empresas_inmobiliarias.form.validacion.email_patron')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="FechaCreacion" editor-type="dxDateBox" :col-span="2">
            <DxLabel :text="$t('empresas_inmobiliarias.form.fecha_creacion')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.fecha_creacion')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="FormaJuridica" editor-type="dxSelectBox" :col-span="2"
            :editor-options="getLookup('empresas_inmobiliarias', 'forma_juridica')">
            <DxLabel :text="$t('empresas_inmobiliarias.form.forma_juridica')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="DenoFactura" editor-type="dxTextBox" :col-span="2">
            <DxLabel :text="$t('empresas_inmobiliarias.form.deno_factura')" />
            <DxPatternRule :pattern="denoFacturaPattern"
              :message="$t('mis_datos.datos_fiscales.validacion.deno_factura_patron')" />
            <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.deno_factura')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Iban" editor-type="dxTextBox" :col-span="3" :editor-options="ibanOptions">
            <DxLabel :text="$t('empresas_inmobiliarias.form.iban')" />
          </DxSimpleItem>
          <DxEmptyItem :col-span="3">
          </DxEmptyItem>
        </DxGroupItem>  

        <!-- Datos fiscales -->
        <DxGroupItem :col-count="6" :col-span="6">

          <!-- Aplica IRPF -->
          <DxGroupItem :col-span="2" :visible="!soySociedad">
            <DxSimpleItem>
              <DxRadioGroup v-model="aplicarIrpf" :items="radioYesNoValues" value-expr="valor" display-expr="texto"
                layout="horizontal" validation-message-mode="always">
              </DxRadioGroup>
              <DxLabel :text="$t('empresas_colaboradoras.form.aplica_irpf')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="PorcentajeIrpf" editor-type="dxNumberBox" :editor-options="irpfNumberOptions"
              :visible="aplicarIrpf">
              <DxLabel :text="$t('empresas_colaboradoras.form.irpf')" />
              <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
                :message="$t('empresas_colaboradoras.form.validacion.irpf_mayor_0')" />
            </DxSimpleItem>
          </DxGroupItem>

          <!-- Con autofactura -->
          <DxGroupItem :col-span="2">
            <DxSimpleItem>
              <DxRadioGroup v-model="conAutofactura" :items="radioYesNoValues" value-expr="valor" display-expr="texto"
                layout="horizontal" validation-message-mode="always">
              </DxRadioGroup>
              <DxLabel :text="$t('empresas_colaboradoras.form.con_autofactura')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="DiasAutofacturacion" editor-type="dxTagBox" :col-span="2"
              :editor-options="diasAutofacturacionOptions" :visible="conAutofactura">
              <DxLabel :text="$t('empresas_colaboradoras.form.dias_autofacturacion')" />
            </DxSimpleItem>
          </DxGroupItem>

          <DxEmptyItem :col-span="2"></DxEmptyItem>

          <DxEmptyItem :col-span="2" :visible="soySociedad"></DxEmptyItem>
        </DxGroupItem>

        <!-- Dirección física -->
        <DxGroupItem :col-count="6" :col-span="6">
          <DxSimpleItem :col-span="6">
            <span class="dx-field-item-label-text text-success">
              {{ $t("empresas_inmobiliarias.form.direccion_fisica") }}
            </span>
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.CodigoPostal" editor-type="dxTextBox" :col-span="1">
            <DxLabel :text="$t('empresas_inmobiliarias.form.codigo_postal')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.codigo_postal')" />
            <DxPatternRule :pattern="codigoPostalPattern"
              :message="$t('empresa_inmobiliaria.form.validacion.codigo_postal_patron')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Provincia" editor-type="dxTextBox" :col-span="2">
            <DxLabel :text="$t('empresas_inmobiliarias.form.provincia')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.provincia')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Municipio" editor-type="dxTextBox" :col-span="3">
            <DxLabel :text="$t('empresas_inmobiliarias.form.municipio')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.municipio')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.TipoVia" editor-type="dxTextBox" :col-span="2">
            <DxLabel :text="$t('empresas_inmobiliarias.form.tipo_de_via')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.tipo_de_via')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.NombreVia" editor-type="dxTextBox" :col-span="4">
            <DxLabel :text="$t('empresas_inmobiliarias.form.nombre_via')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.nombre_via')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Numero" editor-type="dxTextBox" :col-span="1">
            <DxLabel :text="$t('empresas_inmobiliarias.form.numero')" />
            <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.numero')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Escalera" editor-type="dxTextBox" :col-span="1">
            <DxLabel :text="$t('empresas_inmobiliarias.form.escalera')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Piso" editor-type="dxTextBox" :col-span="1">
            <DxLabel :text="$t('empresas_inmobiliarias.form.piso')" />
          </DxSimpleItem>
          <DxSimpleItem data-field="Direccion.Puerta" editor-type="dxTextBox" :col-span="1">
            <DxLabel :text="$t('empresas_inmobiliarias.form.puerta')" />
          </DxSimpleItem>
          <DxEmptyItem :col-span="2">
          </DxEmptyItem>
        </DxGroupItem>

        <!-- Logo -->
        <DxGroupItem :col-count="6" :col-span="6">
          <DxSimpleItem :col-span="3">
            <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderValueChanged">
            </DxFileUploader>
            <DxLabel :text="$t('empresas_inmobiliarias.form.imagen')" />
          </DxSimpleItem>
          <DxGroupItem :col-span="3">
            <DxSimpleItem :col-span="3">
              <div v-show="tieneImagen">
                <img :src="props.empresaInmobiliaria?.ImagenB64!" alt="Imagen de la empresa de agentes"
                  style="max-width: 100%; margin-top: 10px; max-height: 150px" />
              </div>
            </DxSimpleItem>
          </DxGroupItem>
        </DxGroupItem>

        <!-- Logo portal -->
        <DxGroupItem :col-count="6" :col-span="6">
          <DxSimpleItem :col-span="3">
            <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderLogoPortalValueChanged">
            </DxFileUploader>
            <DxLabel :text="$t('mis_datos.datos_fiscales.logoPortal')" />
          </DxSimpleItem>
          <DxGroupItem :col-span="3">
            <DxSimpleItem :visible="props.empresaInmobiliaria?.LogoPortalB64 != null && props.empresaInmobiliaria?.LogoPortalB64 != ''">
              <div class="d-flex justify-content-center">
                <img :src="props.empresaInmobiliaria.LogoPortalB64!" alt="Logo del portal de la empresa inmobiliaria" style="max-width: 60px; max-height: 60px; margin-top: 10px;" />
              </div>
            </DxSimpleItem>
          </DxGroupItem>
        </DxGroupItem>

        <!-- Botón de guardar -->
        <DxGroupItem :col-count="6" :col-span="6">
          <DxEmptyItem :col-span="2"></DxEmptyItem>
          <DxButtonItem :col-span="4" :button-options="optSaveButton"></DxButtonItem>
        </DxGroupItem>

        <template #whitelistLabel="{ data }">
          <div id="template-content">
            <span class="dx-field-item-label-text">{{
              $t('empresas_colaboradoras.form.con_whitelist_servicios') }}</span>
            <div class="info-icon d-inline text-primary ms-1">
              <i class="fas fa-info-circle" id="whitelistLabel"></i>
              <DxTooltip :hide-on-outside-click="false" target="#whitelistLabel" show-event="mouseenter"
                hide-event="mouseleave">
                <template #content>
                  <div id="tooltip-content">{{
                    $t('empresas_colaboradoras.form.texto_informativo_whitelist_servicios') }}</div>
                </template>
              </DxTooltip>
            </div>
          </div>
        </template>
      </DxForm>
    </div>
  </div>
</template>


<script setup lang="ts">
import { useIntl } from '@services/i18n';
import { useApi } from '@api/integration';
import { DxRadioGroup } from 'devextreme-vue';
import { ApiError } from '@/.shared/services/http';
import { DxTooltip } from 'devextreme-vue/tooltip';
import { useIbanField } from "@composables/devextreme";
import { saveButtonProps } from '@helpers/button-helper';
import validationEngine from "devextreme/ui/validation_engine";
import { DxFileUploader, DxTagBox, } from 'devextreme-vue';
import { getLookup, getColumnsLookupAsNumber } from '@helpers/grid-helper';
import { EstadoEmpresaAgentesEnum, FormaJuridicaEnum, type EditEmpresaInmobiliariaModel } from '@api/api';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF } from '@/.shared/errors/codes';
import { useLogoStore } from '@/composables/useLogoStore';
import { computed, onBeforeMount, onMounted, reactive, ref, watch, } from 'vue';
import {
  DxEmailRule,
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxPatternRule,
  DxCustomRule,
  DxCompareRule,
  DxEmptyItem,
  DxGroupItem,
  DxButtonItem,
DxSimpleItem
} from "devextreme-vue/form";
import type { EditEmpresaInmobiliaria } from "./models/EditEmpresaInmobiliaria";
import { recargarDatosEmpresaMisDatos } from "@/composables/misDatosRecargaEmpresa";
import {
  crearValidadorCampoInmutable,
  useEmpresaAgentesCamposIdentidadBloqueados,
} from "@/composables/empresaAgentesCamposIdentidadBloqueados";

const props = defineProps({
  empresaInmobiliaria: {
    type: Object as () => EditEmpresaInmobiliaria,
    required: true
  }
});
const model = computed(() => { return props.empresaInmobiliaria; });

const api = useApi();
const intl = useIntl();
const { refreshLogo } = useLogoStore();

const datosOriginales = ref<{ RazonSocial?: string; Documento?: string }>();
const { camposIdentidadBloqueados } = useEmpresaAgentesCamposIdentidadBloqueados();
const validarRazonSocial = crearValidadorCampoInmutable(() => datosOriginales.value?.RazonSocial, camposIdentidadBloqueados);
const validarDocumento = crearValidadorCampoInmutable(() => datosOriginales.value?.Documento, camposIdentidadBloqueados);

const sincronizarDatosOriginales = (): void => {
  const empresa = props.empresaInmobiliaria;
  if (!empresa) {
    return;
  }
  datosOriginales.value = {
    RazonSocial: empresa.RazonSocial,
    Documento: empresa.Documento,
  };
};

const formInstance = ref<DxForm>();
const estadoEmpresaTooltipId = "estadoEmpresaInmobiliariaInfo";
const aplicarIrpf = ref<boolean>(false);
const conAutofactura = ref<boolean>(false);

const telefonoPattern = ref<RegExp>(/^\d{9}$/);
const codigoPostalPattern = ref<RegExp>(/^\d{5}$/);
const denoFacturaPattern = ref<RegExp>(/^[A-Za-zÑñáéíóúüÁÉÍÓÚÜ0-9]{1,7}$/);
const cifPattern = ref<RegExp>(/^[A-Za-z]\d{7}[A-Za-z0-9]$|^\d{8}[A-Za-z]$/);

const { getIbanFieldOptions } = useIbanField();
const ibanOptions = getIbanFieldOptions();

const radioYesNoValues = [
  { valor: true, texto: intl.i18n.t("shared.ui.literals.yes") },
  { valor: false, texto: intl.i18n.t("shared.ui.literals.no") },
];

const irpfNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal');
    }
  },
  min: 0,
  max: 100
};

const optSaveButton = {
  ...saveButtonProps(),
  onClick: function () {
    const validationResult = formInstance.value?.instance?.validate();
    if (validationResult?.isValid) {
      guardarDatos();
    }
  }
};

const diasAutofacturacionOptions = {
  dataSource: getColumnsLookupAsNumber('empresas_inmobiliarias', 'dias_autofacturacion').dataSource,
  valueExpr: 'value',
  displayExpr: 'text'
};

watch(
  () => [props.empresaInmobiliaria?.RazonSocial, props.empresaInmobiliaria?.Documento],
  () => sincronizarDatosOriginales(),
  { immediate: true },
);

onMounted(async () => {
  conAutofactura.value = props.empresaInmobiliaria?.DiasAutofacturacion ? props.empresaInmobiliaria.DiasAutofacturacion.length > 0 : false;
  aplicarIrpf.value = props.empresaInmobiliaria?.PorcentajeIrpf ? (props.empresaInmobiliaria.PorcentajeIrpf != null && props.empresaInmobiliaria.PorcentajeIrpf != 0) : false;
});

watch(() => aplicarIrpf.value, (newVal) => {
  if (!newVal)
    props.empresaInmobiliaria!.PorcentajeIrpf = 0;
});

watch(() => conAutofactura.value, (newVal) => {
  if (!newVal)
    props.empresaInmobiliaria!.DiasAutofacturacion = [];
});

const tieneImagen = computed(() => { return !!props.empresaInmobiliaria?.ImagenB64; });
const soySociedad = computed(() => { return props.empresaInmobiliaria?.FormaJuridica == FormaJuridicaEnum.SociedadLimitada; });
const esEmpresaRechazada = computed(() => props.empresaInmobiliaria?.IdEstado === EstadoEmpresaAgentesEnum.Rechazada);

function getSelectorEmpresaServiciosProperties(): DxTagBox {
  return {
    valueExpr: 'Id',
    displayExpr: 'Denominacion',
    searchEnabled: true
  }
};

function onFileUploaderValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (props.empresaInmobiliaria != null)
        props.empresaInmobiliaria.ImagenB64 = base64;
    };
    reader.readAsDataURL(file);
  }
};

function onFileUploaderLogoPortalValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (props.empresaInmobiliaria != null)
        props.empresaInmobiliaria.LogoPortalB64 = base64;
    };
    reader.readAsDataURL(file);
  }
}

const guardarDatos = async () => {
  var res = validationEngine.validateGroup("validacionEmpresaInmobiliaria");
  if (res.isValid) {
    try {
      await api.callStandardApi("editEmpresaInmobiliaria", { showLoading: true }, model.value as EditEmpresaInmobiliariaModel)
        .then(async () => {
          await recargarDatosEmpresaMisDatos();
          sincronizarDatosOriginales();
          toastSuccess(intl.i18n.t("empresa_inmobiliaria.edicion_exito"));
          refreshLogo();
        });
    } catch (e: any) {
      if (e instanceof ApiError && e.details?.errorList.length === 1 && [COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF].includes(e.details.errorList[0].Code)) {
        toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
      }
      else throw e;
      return false;
    }
    return true;
  } else {
    toastError(intl.i18n.t('empresa_inmobiliaria.revise_validacion'));
  }
};

const saveButtonOptions = {
  useSubmitBehavior: false,
  onClick: guardarDatos
};

</script>

<style lang="scss" scoped>
.status-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 5px;
  text-align: center;
}
</style>